import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Button,
    InputAdornment,
    TextField,
    Grid,
    Stack,
    Avatar,
    Tooltip,
    Autocomplete,
    Drawer,
    Divider,
} from "@mui/material";
import "../../App.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { TableLoader } from "../../components/TableLoader";
import GroupIcon from "@mui/icons-material/Group";
import { useSnackbar } from "../../providers/SnackbarProvider";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomPagination from "../../components/CustomPagination";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { FileClock, MessagesSquare, Timeline } from "lucide-react";
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';

const StudentsList = () => {

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    const tableref = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, dashboard, instituteId, instituteName, TrainerName } =
        location.state || "";
    const [showTable, setShowTable] = useState(false);
    const [openDrawer, setOpen] = useState(false);
    const [appliedInstitute, setAppliedInstitute] = useState(
        instituteName
            ? instituteName
            : localStorage.getItem("applyInstitute")
                ? localStorage.getItem("applyInstitute")
                : null
    );
    const [appliedTrainer, setAppliedTrainer] = useState(
        TrainerName
            ? TrainerName
            : localStorage.getItem("applyTrainer")
                ? localStorage.getItem("applyTrainer")
                : null
    );
    const [selectedTrainerName, setSelectedTrainerName] = useState("");
    const [selectedCollegeName, setSelectedCollegeName] = useState("");

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const [searchKey, setSearchKey] = useState(
        localStorage.getItem("searchvalue") || ""
    );
    const debouncedSearch = useDebounce(searchKey);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [listData, setListData] = useState([]);
    const [selectedCollege, setSelectedCollege] = useState(
        instituteId
            ? instituteId
            : +localStorage.getItem("Institute_Id_Filter") ||
            +localStorage?.getItem("InstituteId") ||
            ""
    );
    const [colleges, setColleges] = useState([]);
    const [trainersList, setTrainersList] = useState([]);
    const [batchOptions, setBatchOptions] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [appliedBatch, setAppliedBatch] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(
        userId ? userId : +localStorage?.getItem("Trainer_Id_Filter") || ""
    );
    const { showSnackbar } = useSnackbar();
    // Pagination state
    const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
    const [rowsPerPage, setRowsPerPage] = useState(
        +localStorage.getItem("rowsPerPage") || 10
    );

    const [Trainers, setTrainers] = useState([]);

    const handleSearch = (e) => {
        localStorage.setItem("currentPage", 0);
        setPage(0);
        localStorage.setItem("searchvalue", e.target.value);
        setSearchKey(e.target.value);
    };

    const handleAddStudent = (isAdd, id) => {
        navigate("/add-interviewer", {
            state: {
                addInterviewer: isAdd,
                InterviewerId: id,
            },
        });
    };

    const handleClickClear = () => {
        tableref.current = {};
        setPage(0);
        // Only clear institute if SUPER_ADMIN, otherwise keep their institute
        if (localStorage.getItem("role") === "SUPER_ADMIN") {
            setSelectedCollege(null);
        } else {
            setSelectedCollege(+localStorage?.getItem("InstituteId") || "");
        }
        setSelectedTrainer(null);
        setAppliedTrainer(null);
        setAppliedInstitute(null);
        setSelectedBatch(null);
        setAppliedBatch(null);
        localStorage.removeItem("Institute_Id_Filter");
        localStorage.removeItem("applyInstitute");
        localStorage.removeItem("applyTrainer");
        localStorage.removeItem("Trainer_Id_Filter");
        localStorage.removeItem("applyBatch");

        navigate(location?.pathname, {
            replace: true,
            state: {
                ...location?.state,
                TrainerName: null,
                userId: null,
                instituteName: null,
                instituteId: null,
            },
        });

        // Trigger API call with cleared values
        setTimeout(() => studentList(), 0);
    };

    const handleApplyInstituteAndTrainer = () => {
        tableref.current = {};
        setPage(0);

        setAppliedInstitute(selectedCollegeName);
        localStorage.setItem("applyInstitute", selectedCollegeName);

        setAppliedTrainer(selectedTrainerName);
        localStorage.setItem("applyTrainer", selectedTrainerName);

        setAppliedBatch(selectedBatch);
        localStorage.setItem("applyBatch", selectedBatch ? JSON.stringify(selectedBatch) : "");

        setOpen(false); // close drawer after applying
    };

    const studentList = async () => {
        setLoading(true);

        const cachekey = `page_${page}_size_${rowsPerPage}_searchkey_${debouncedSearch}_selectedCollege_${selectedCollege}_trainer_${selectedTrainer}_batch_${appliedBatch?.batch_id}`;

        if (tableref.current[cachekey]) {
            const cached = tableref.current[cachekey];
            setListData(cached?.data);
            setCount(cached?.count);
            setLoading(false);
            return;
        }

        try {
            const payload = {
                page: page,
                size: rowsPerPage,
                search_key: debouncedSearch,
            };
            const res = await api.post("ListInterviewers", payload);
            if (res?.data?.status === "success") {
                const data = res?.data?.data;
                const count = res?.data?.count;
                const trainer_names = res?.data?.data?.trainer_names;

                tableref.current[cachekey] = {
                    data,
                    count,
                    trainer_names,
                };

                setListData(res?.data?.data);
                setCount(res?.data?.count);
                setLoading(false);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.log("Error :", error);
            setLoading(false);
        }
    };

    const instituteList = async () => {
        try {
            const res = await api.post("/ListInstituteNames");
            if (res?.data?.status === "success") {
                setColleges(res?.data?.data);
            }
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Failed to fetch institutes.");
        }
    };

    const trainerList = async (id) => {
        try {
            const payload = {
                institute_id: id
                    ? id
                    : +localStorage?.getItem("InstituteId") || selectedCollege || 0,
            };
            const res = await api.post("/ListTrainersForFilter", payload);
            if (res?.data?.status === "success") {
                setTrainersList(res?.data?.data);
            }
        } catch (error) {
            console.log("Error :", error);
            showSnackbar("error", "Failed to fetch institutes.");
        }
    };

    const batchList = async () => {
        try {
            const { data } = await api.post("getAllBatchList");
            setBatchOptions(data?.data ?? []);
        } catch (error) {
            console.error("Error fetching batch options:", error);
        }
    };

    useEffect(() => {
        studentList();
    }, [page, rowsPerPage, debouncedSearch, appliedInstitute, appliedTrainer, appliedBatch]);

    useEffect(() => {
        instituteList();
        batchList();
        if (selectedCollege) {
            trainerList();
        }
    }, []);

    // Load batch filter from localStorage on mount
    useEffect(() => {
        const savedBatch = localStorage.getItem("applyBatch");
        if (savedBatch) {
            setAppliedBatch(JSON.parse(savedBatch));
            setSelectedBatch(JSON.parse(savedBatch));
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTable(true);
        }, 200); // 0.2 sec delay

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        localStorage.setItem("currentpage", page);
        localStorage.setItem("rowsPerPage", rowsPerPage);
    }, [page, rowsPerPage]);

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            {dashboard === true && (
                <Grid
                    item
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#1c97e3",
                        gap: "4px",
                        cursor: "pointer",
                        width: "fit-content",
                        mb: 2,
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            window.history.back();
                        }
                    }}
                    onClick={() => window.history.back()}
                >
                    <ArrowBackIcon sx={{ fontSize: "17px" }} />
                    <Typography
                        sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
                    >
                        Back
                    </Typography>
                </Grid>
            )}

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                sx={{
                    // borderBottom: "1px solid #828487",
                    pb: 2,
                }}
            >
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 0.5,
                        alignItems: "center",
                    }}
                >
                    <Box className="icon-box">
                        <MessagesSquare style={{ width: '32px', height: '32px', color: '#1e88e5' }} />
                    </Box>
                    {/* Title */}
                    <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a", marginLeft: 1 }}>
                        {getLabel('INTERVIEWER')}
                    </Typography>
                </Grid>
                {/* Search + Button */}
                <Box display="flex" gap={4} alignItems="center">
                    <TextField
                        value={searchKey}
                        onChange={(e) => handleSearch(e)}
                        size="small"
                        variant="outlined"
                        placeholder={` Search ${getLabel('INTERVIEWER')}`}
                        className="custom-search-field"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {(localStorage.getItem("role") === "TRAINER" ||
                        localStorage.getItem("role") === "INSTITUTE_ADMIN") && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                className="gradient-button"
                                onClick={() => handleAddStudent("true", null)}
                            >
                                Add {getLabel('INTERVIEWER')}
                            </Button>
                        )}
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    mt: 3,
                    opacity: showTable ? 1 : 0,
                    transform: showTable ? "translateY(0)" : "translateY(40px)",
                    transition: "opacity 0.5s ease, transform 0.5s ease",
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow className="tableHeader">
                            <TableCell className="tableHeader">
                                <b>S.No</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Name</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Contact</b>
                            </TableCell>
                            {localStorage.getItem("role") === "SUPER_ADMIN" && (
                                <TableCell className="tableHeader">
                                    <b>{`${getLabel('INSTITUTE')}`}</b>
                                </TableCell>
                            )}
                            <TableCell className="tableHeader">
                                <b>City</b>
                            </TableCell>
                            <TableCell className="tableHeader">
                                <b>Designation</b>
                            </TableCell>

                            <TableCell className="tableHeader">
                                <b>Status</b>
                            </TableCell>

                            <TableCell className="tableHeader">
                                <b>Actions</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {loading ? (
                        <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                            <TableLoader message="Fetching students..." />
                        </TableCell>
                    ) : (
                        <TableBody className="tablebody">
                            {listData.map((item, index) => (
                                <TableRow
                                    key={item.assessment_id}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                                        "&:hover": { backgroundColor: "#f1f1f1" },
                                    }}
                                >
                                    <TableCell align="center">
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: "bold" }}>
                                            {item.first_name + " " + item.last_name}
                                        </span>
                                        <br />
                                        {item?.gender}
                                    </TableCell>
                                    <TableCell>
                                        {item?.phone}
                                        <br />
                                        {item?.email}
                                    </TableCell>
                                    {localStorage.getItem("role") === "SUPER_ADMIN" && (
                                        <TableCell>{item.institute_name}</TableCell>
                                    )}
                                    <TableCell>{item.city || "N/A"}</TableCell>
                                    <TableCell sx={{ textWrap: "nowrap" }}>
                                        {item?.interviewer_profile?.designation
                                            ? item?.interviewer_profile?.designation
                                            : "-"}
                                    </TableCell>

                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "inline-block",
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: "12px",
                                                fontWeight: "bold",
                                                fontSize: "12px",
                                                backgroundColor:
                                                    item.status === "ACTIVE"
                                                        ? "#d4edda"
                                                        : item.status === "INACTIVE"
                                                            ? "#f8d7da"
                                                            : "#f1f1f1",
                                                color:
                                                    item.status === "ACTIVE"
                                                        ? "#155724"
                                                        : item.status === "INACTIVE"
                                                            ? "#721c24"
                                                            : "#333",
                                                textAlign: "center",
                                            }}
                                        >
                                            {item.status}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            display="flex"
                                            justifyContent="left"
                                            alignItems="center"
                                            gap={0.5}
                                        >
                                            {/* View Button */}
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigate("/view-interviewer", {
                                                        state: {
                                                            addInterviewer: "false",
                                                            InterviewerId: item?.user_id,
                                                        },
                                                    });
                                                    handleClose();
                                                }}
                                                sx={{
                                                    display: "flex",
                                                    // alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <VisibilityIcon color="primary" />
                                                <Typography color="primary" sx={{ fontSize: "12px" }}>
                                                    View
                                                </Typography>
                                            </IconButton>

                                            {/* Interview History */}
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigate("/interview-scheduler-list");
                                                }}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <FileClock size={20} strokeWidth={2} style={{ color: '#1C97E3' }} />
                                                <Typography color="primary" sx={{ fontSize: "12px", color: '#1C97E3' }}>
                                                    Interview History
                                                </Typography>
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {((page === 0 && listData?.length > rowsPerPage - 1) ||
                                page !== 0) && (
                                    <CustomPagination
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        count={count}
                                        setPage={setPage}
                                        colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8}
                                    />
                                )}

                            {listData?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                                        <TableNodata />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </Box>
    );
};

export default StudentsList;
